import type { CodeToolScriptApi } from "@repo/app-types/entities/tools/codeToolScriptApi";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { FieldError } from "@repo/ui/field/fieldError";
import { useId, useMemo, type FC } from "react";
import {
  FieldDiffToggle,
  useFieldDiffState,
} from "../../../components/diffableField.js";
import { LazyCodeEditor } from "./lazyCodeEditor.js";

/** Where code fails to compile, as the API reports it: 1-based. */
export interface CodeToolErrorPosition {
  line: number;
  column: number;
}

interface CodeToolCodeFieldProps {
  value: string;
  /** The saved code to diff against; `undefined` for a new tool. */
  originalValue: string | undefined;
  onChange: (value: string) => void;
  errorMessage: string | undefined;
  /** Marks the error in the editor, when the API said where it is. */
  errorPosition: CodeToolErrorPosition | undefined;
  /** What the script can reach, for completions. */
  scriptApi: CodeToolScriptApi;
  isReadOnly: boolean;
  /** Shows the field's changes. When set, the toggle doesn't render. */
  isShowingChanges?: boolean;
}

export const CodeToolCodeField: FC<CodeToolCodeFieldProps> = (props) => {
  const {
    value,
    originalValue,
    onChange,
    errorMessage,
    errorPosition,
    scriptApi,
    isReadOnly,
    isShowingChanges,
  } = props;

  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();

  // Kept stable so the editor only resets its diagnostics when they change.
  const diagnostic = useMemo(
    () =>
      errorMessage !== undefined && errorPosition
        ? { message: errorMessage, ...errorPosition }
        : undefined,
    [errorMessage, errorPosition],
  );
  const diff = useFieldDiffState(originalValue, value);
  const isDiffing =
    diff.hasChanges && (isShowingChanges ?? diff.isShowingChanges);

  return (
    <div>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        {/* Not a <label>: the editor is no form control for a click to focus,
            so it names the editor through aria-labelledby instead. */}
        <div
          id={labelId}
          className="text-sm leading-6 font-semibold text-foreground"
        >
          Code
        </div>
        {diff.hasChanges && isShowingChanges === undefined ? (
          <FieldDiffToggle
            isShowingChanges={diff.isShowingChanges}
            onToggle={diff.onToggle}
          />
        ) : null}
      </div>
      <LazyCodeEditor
        language="javascript"
        minHeight={240}
        value={value}
        onChange={onChange}
        originalValue={isDiffing ? originalValue : undefined}
        collapseUnchanged
        hasDiffMinimap
        aria-labelledby={labelId}
        aria-describedby={`${descriptionId} ${errorId}`}
        hasError={errorMessage !== undefined}
        diagnostic={diagnostic}
        isReadOnly={isReadOnly}
        scriptApi={scriptApi}
      />
      {errorMessage ? (
        <FieldError id={errorId}>{errorMessage}</FieldError>
      ) : (
        <FieldDescription id={descriptionId}>
          A JavaScript module whose default export is an async function. It is
          given the call&apos;s input and the tool&apos;s secrets, and can{" "}
          <code>fetch</code> from the allowed domains. What it returns is the
          tool&apos;s answer. Press Esc then Tab to leave the editor.
        </FieldDescription>
      )}
    </div>
  );
};
